import axios, { AxiosError } from 'axios';

interface CnpjResponse {
  razao_social: string;
  estabelecimento: {
    ddd1: string;
    ddd2: string;
    telefone1: string;
    telefone2: string;
    email: string;
    logradouro: string;
    numero: string;
    complemento: string;
    cep: string;
    cidade: {
      nome: string;
    };
    estado: {
      sigla: string;
    };
    inscricoes_estaduais?: {
      inscricao_estadual: string;
    }[];
    atividade_principal?: {
      descricao: string;
    };
  };
}

interface CnpjValidationResult {
  success: boolean;
  data?: CnpjResponse;
  error?: {
    message: string;
    status?: number;
  };
}

// Add these new interfaces for the ViaCEP API
interface CepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
}

interface CepValidationResult {
  success: boolean;
  data?: CepResponse;
  error?: {
    message: string;
    status?: number;
  };
}

/**
 * Validates a CNPJ number through an external API
 * @param cnpj CNPJ number to validate (only digits)
 * @returns Object containing success status and data or error
 */
export const validateCnpj = async (cnpj: string): Promise<CnpjValidationResult> => {
  try {
    const cleanCnpj = cnpj.replace(/[^\d]+/g, '');
    const response = await axios.get<CnpjResponse>(`https://publica.cnpj.ws/cnpj/${cleanCnpj}`);

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError;

    return {
      success: false,
      error: {
        message: getErrorMessage(axiosError),
        status: axiosError.response?.status,
      },
    };
  }
};

/**
 * Extract error message from axios error
 */
const getErrorMessage = (error: AxiosError): string => {
  if (error.response) {
    // Server responded with a non-2xx status
    switch (error.response.status) {
      case 404:
        return 'CNPJ não encontrado na base de dados';
      case 429:
        return 'Muitas requisições. Por favor, aguarde um momento antes de tentar novamente';
      default:
        return 'Erro ao consultar CNPJ. Tente novamente mais tarde';
    }
  } else if (error.request) {
    // The request was made but no response was received
    return 'Erro de conexão ao consultar CNPJ';
  } else {
    // Something happened in setting up the request
    return error.message || 'Erro desconhecido ao consultar CNPJ';
  }
};

/**
 * Fetches address information from a CEP using the ViaCEP API
 * @param cep CEP number to lookup (only digits)
 * @returns Object containing success status and address data or error
 */
export const fetchAddressByCep = async (cep: string): Promise<CepValidationResult> => {
  try {
    const cleanCep = cep.replace(/[^\d]+/g, '');

    if (cleanCep.length !== 8) {
      return {
        success: false,
        error: {
          message: 'CEP deve conter 8 dígitos',
        },
      };
    }

    const response = await axios.get<CepResponse>(`https://viacep.com.br/ws/${cleanCep}/json/`);

    // ViaCEP returns a property "erro: true" when the CEP is not found
    if (Object.prototype.hasOwnProperty.call(response.data, 'erro')) {
      return {
        success: false,
        error: {
          message: 'CEP não encontrado',
        },
      };
    }

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError;

    return {
      success: false,
      error: {
        message: getCepErrorMessage(axiosError),
        status: axiosError.response?.status,
      },
    };
  }
};

/**
 * Extract error message from ViaCEP API errors
 */
const getCepErrorMessage = (error: AxiosError): string => {
  if (error.response) {
    // Server responded with a non-2xx status
    switch (error.response.status) {
      case 404:
        return 'CEP não encontrado';
      default:
        return 'Erro ao consultar CEP. Tente novamente mais tarde';
    }
  } else if (error.request) {
    // The request was made but no response was received
    return 'Erro de conexão ao consultar CEP';
  } else {
    // Something happened in setting up the request
    return error.message || 'Erro desconhecido ao consultar CEP';
  }
};
